import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';
import { SERVICE_KEYS, type ServiceKey } from '@shared/api/config';
import { useSessionStore } from '@shared/auth/session';

import { grantBody, targetsFor, wireRoleName } from '../_shared/grants';
import type { UserEntitlement, UserId } from '../_shared/types';
import { adminUserKey, userEntitlementsKey } from './queries';

/**
 * The four writes the account detail owns. None auto-retries and none is
 * optimistic — a role, an entitlement, and a block are all authoritative facts,
 * and a panel that repaints before the server agreed is a claim the operator
 * cannot see through.
 */

/**
 * A role change invalidates the account, the roster, and the users report — and,
 * **when the account is the signed-in operator's own**, the session bootstrap
 * and every service's permission read as well.
 *
 * An `admin` may write any account's role including their own; nothing durably
 * records it while the audit log is deferred, so the confirmation copy naming
 * the escalation is the only guard that ships. What the console must not do is
 * leave that operator holding a stale answer about their own reach: the sidebar,
 * the row-level actions, and the guard all read from those queries.
 */
function useRoleInvalidation(userId: UserId) {
  const queryClient = useQueryClient();
  const selfId = useSessionStore((state) => state.profile?.id ?? null);

  return () => {
    void queryClient.invalidateQueries({ queryKey: adminUserKey(userId) });
    void queryClient.invalidateQueries({ queryKey: key('auth', 'adminUsers') });
    void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'users') });

    if (selfId !== userId) return;

    void queryClient.invalidateQueries({ queryKey: key('auth', 'me') });
    for (const service of SERVICE_KEYS) {
      void queryClient.invalidateQueries({ queryKey: key(service, 'permissions') });
    }
  };
}

/**
 * The account role, on **every** service's own route.
 *
 * `PUT` rather than a `:verb` because the console renders one `<select>` and
 * submits a value, not a delta — and `null` is the revoke, which is why grant
 * and revoke are one control rather than two verbs.
 *
 * **The fan-out is the console's, and it was missing.** `backend-auth` refuses
 * to proxy a peer's grant and says so at its own route; six peers state the
 * other half on their own role-grant gate and admit this console's `admin`
 * session for exactly that reason. Until now this mutation wrote `auth` alone,
 * so an account granted «مشغّل» here held `operator` in one service and the
 * default role in the other eight — and, with the sidebar drawn from what each
 * service answers, signed in to a console with almost nothing in it.
 *
 * **`auth` is written first, alone, and a failure there stops the rest.** It is
 * the row the roster and this page render, so a run that wrote six peers and
 * failed on `auth` would leave the console showing a role the account does not
 * hold. The peers then go out together and their failures are **reported, not
 * thrown** — six services are six independent writes with no transaction across
 * them, and the honest answer to a partial write is which parts landed.
 */
export function useReplaceAccountRole(userId: UserId) {
  const invalidate = useRoleInvalidation(userId);
  const grantedBy = useSessionStore((state) => state.profile?.id ?? null);

  return useMutation({
    mutationFn: async (selected: string): Promise<ServiceKey[]> => {
      if (grantedBy === null) throw new Error('no session');

      const roleName = wireRoleName(selected);
      const targets = targetsFor(roleName);

      const auth = targets.find((target) => target.service === 'auth');
      if (auth) {
        await api.put<null>('auth', `/admin/userRoles/user/${userId}`, grantBody(auth, roleName, grantedBy));
      }

      const peers = targets.filter((target) => target.service !== 'auth');
      const answers = await Promise.allSettled(
        peers.map((target) =>
          api.put<null>(
            target.service,
            `/admin/userRoles/user/${userId}`,
            grantBody(target, roleName, grantedBy),
          ),
        ),
      );

      return peers
        .filter((_target, index) => answers[index]?.status === 'rejected')
        .map((target) => target.service);
    },
    onSuccess: invalidate,
    retry: false,
  });
}

/**
 * One field, two editors, one column.
 *
 * The holder writes the same value from their own settings; there is no third
 * copy and no draft, last write wins, and the console's confirm-shaped dialog is
 * what stands between a correction and an accident. The service refuses the
 * write on an account that holds no role — which is the same condition that
 * decides whether this section is drawn at all.
 */
export function useUpdateBiography(userId: UserId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (biography: string) =>
      api.patch<null>('auth', `/admin/users/${userId}/biography`, { biography }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminUserKey(userId) });
    },
    retry: false,
  });
}

/**
 * The manual grant.
 *
 * **The scope is not submitted.** `backend-finance` writes it from
 * `DEFAULT_ENTITLEMENT_SCOPE` at insert — the same rule its plan-create route
 * states in as many words — so a body carrying a scope would let this form
 * choose a setting the platform already chose. The dialog says what the grant
 * opens instead of asking.
 *
 * `ends_at` is sent as an instant, because the column is a `TIMESTAMPTZ` and the
 * wire is ISO-8601 UTC. The operator picks a date; the boundary converts once,
 * here, rather than leaving a bare `YYYY-MM-DD` for the service to interpret.
 *
 * **No `Idempotency-Key`.** The header is scoped platform-wide to finance-bound
 * *money-moving* writes, and a manual grant moves no money; the server's own
 * refusal on an overlapping live window is what makes a double press safe.
 */
export function useGrantEntitlement(userId: UserId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (endsAtDate: string) =>
      api.post<UserEntitlement>('finance', `/admin/users/${userId}/entitlements:grant`, {
        ends_at: new Date(`${endsAtDate}T00:00:00Z`).toISOString(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userEntitlementsKey(userId) });
      void queryClient.invalidateQueries({ queryKey: key('finance', 'subscriptions') });
      void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'revenue') });
    },
    retry: false,
  });
}
